<script setup lang="ts">
  import { type Stakeholder } from '@gat-ship/types'
  import { IconLoading } from '@gat-ship/icons'
  import { Button } from '@gat-ship/vue-components'
  import { computed, ref } from 'vue'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import {
    useStakeholderEditAction,
    type StakeholderEditButtonConfig
  } from '../../composables/useStakeholderEditAction'
  import { text } from '@gat-ship/stakeholders'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'

  const props = defineProps<{ stakeHolder: Stakeholder }>()

  const service = useStakeholderContext()
  const emit = defineEmits<{
    (e: 'saving'): void
    (e: 'deleted'): void
  }>()

  const { isLoading, canShow, btnConfig } = useStakeholderEditAction(
    () => props.stakeHolder,
    () =>
      text.groups.removeStakeholder(props.stakeHolder.stakeholderType).buttons
        .removeStakeholder as StakeholderEditButtonConfig
  )

  const showConfirm = ref(false)
  const confirmText = computed(
    () =>
      text.groups.removeStakeholder(props.stakeHolder.stakeholderType)
        .confirmDialog
  )

  async function onDeleteStakeholder() {
    showConfirm.value = false
    emit('saving')
    await service?.commands.remove(props.stakeHolder.id)
    emit('deleted')
  }
</script>

<template>
  <Button
    v-if="canShow"
    :variant="btnConfig.variant"
    :size="btnConfig.size"
    :class="btnConfig.class"
    :disabled="isLoading"
    @click="showConfirm = true"
  >
    <IconLoading v-if="isLoading" class="size-4 shrink-0" />
    <component :is="btnConfig.icon" v-else-if="btnConfig.icon" class="size-4" />
    {{ btnConfig.label }}
  </Button>
  <AlertDialog v-model:open="showConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ confirmText.title }}</AlertDialogTitle>
        <AlertDialogDescription class="whitespace-pre-line">
          {{ confirmText.message(props.stakeHolder.name) }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="onDeleteStakeholder()">
          Confirm
        </Button>
        <Button variant="outline" @click="showConfirm = false"> Cancel </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
